import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { replaceArrayValueByIndex, splitStringToLengthArray, } from '../utils/stringUtils';
import { otpLengthPropType } from './InputOtp.utils';
import { Input } from '../Input';
import { Text } from '../Typography';
import { Layout } from '../Layout';
import { CheckSmallFilled } from '../icons/icons/CheckSmallFilled';
import { WarningFilledSmall } from '../icons/icons/WarningFilledSmall';
/**
 * Define a gap for each OTP length
 * eg default 6 input length has medium gap
 */
var inputGap = {
    4: 'large',
    5: 'medium',
    6: 'medium',
    7: 'x-small',
    8: 'x-small',
};
var InputFeedback = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  gap: 0.25rem;\n"], ["\n  display: flex;\n  align-items: center;\n  gap: 0.25rem;\n"])));
var InputField = styled(Input)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  input {\n    width: 1.5rem;\n    padding: 0;\n    text-align: center;\n\n    /* Hide Arrows From Input Number */\n    &::-webkit-outer-spin-button,\n    &::-webkit-inner-spin-button {\n      -webkit-appearance: none;\n      margin: 0;\n    }\n    &[type='number'] {\n      -moz-appearance: textfield;\n    }\n  }\n"], ["\n  input {\n    width: 1.5rem;\n    padding: 0;\n    text-align: center;\n\n    /* Hide Arrows From Input Number */\n    &::-webkit-outer-spin-button,\n    &::-webkit-inner-spin-button {\n      -webkit-appearance: none;\n      margin: 0;\n    }\n    &[type='number'] {\n      -moz-appearance: textfield;\n    }\n  }\n"])));
/**
 * Generates InputFeedback based on the given parameters.
 * @param {boolean} hasError - Indicates if the input has an error.
 * @param {string | undefined} errorMessage - The error message to be displayed when there is an error.
 * @param {boolean} isValid - Indicates if the input is valid.
 * @param {string | undefined} successMessage - The success message to be displayed when the input is valid.
 * @returns {React.ReactNode} - The InputFeedback component with the appropriate content and styling.
 */
export var getInputFeedback = function (hasError, errorMessage, isValid, successMessage) {
    if (hasError && errorMessage) {
        return (React.createElement(InputFeedback, null,
            React.createElement(WarningFilledSmall, { color: "red", size: 2 / 3 }),
            React.createElement(Text, { color: "danger", size: "small", element: "span" }, errorMessage)));
    }
    if (isValid && successMessage) {
        return (React.createElement(InputFeedback, null,
            React.createElement(CheckSmallFilled, { color: "green", size: 0.666666667 }),
            React.createElement(Text, { color: "success", size: "small", element: "span" }, successMessage)));
    }
    return null;
};
export var InputOtp = styled(function (_a) {
    var id = _a.id, _b = _a.otpLength, otpLength = _b === void 0 ? 6 : _b, isRequired = _a.isRequired, isDisabled = _a.isDisabled, _c = _a.isValid, isValid = _c === void 0 ? false : _c, _d = _a.hasError, hasError = _d === void 0 ? false : _d, errorMessage = _a.errorMessage, successMessage = _a.successMessage, defaultValue = _a.defaultValue, pattern = _a.pattern, _e = _a.clear, clear = _e === void 0 ? false : _e, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus, onKeyDown = _a.onKeyDown, _f = _a.inputType, inputType = _f === void 0 ? 'text' : _f, _g = _a.inputMode, inputMode = _g === void 0 ? 'numeric' : _g, _h = _a.htmlAutoComplete, htmlAutoComplete = _h === void 0 ? 'one-time-code' : _h, className = _a.className, children = _a.children, _j = _a.label, label = _j === void 0 ? 'Please enter OTP character' : _j, htmlAttributes = __rest(_a, ["id", "otpLength", "isRequired", "isDisabled", "isValid", "hasError", "errorMessage", "successMessage", "defaultValue", "pattern", "clear", "onChange", "onBlur", "onFocus", "onKeyDown", "inputType", "inputMode", "htmlAutoComplete", "className", "children", "label"]);
    var items = Array.from(Array(otpLength).keys());
    var inputRefs = items.map(function () { return React.createRef(); });
    var _k = React.useState(defaultValue || Array(otpLength).fill('')), otpValue = _k[0], setOtpValue = _k[1];
    // Reset the otpValue when the clear prop changes
    React.useEffect(function () {
        if (clear) {
            setOtpValue(Array(otpLength).fill(''));
        }
    }, [clear, otpLength]);
    var onChangeHandler = function (index) {
        return function (event) {
            var value = event.target.value;
            // When inputType is 'number', only allow single digit values
            if (inputType === 'number' && !/^\d?$/.test(value)) {
                return;
            }
            var nextRef = inputRefs[index + 1];
            if (value.length === 1 && nextRef && nextRef.current) {
                nextRef.current.focus();
            }
            var newValue = replaceArrayValueByIndex(otpValue, index, value);
            setOtpValue(newValue);
            onChange(event, newValue);
        };
    };
    var onBlurHandler = function (event) {
        return onBlur && onBlur(event, otpValue);
    };
    var onFocusHandler = function (event) {
        return onFocus && onFocus(event, otpValue);
    };
    var onPasteHandler = function (event) {
        event.preventDefault();
        var data = event.clipboardData.getData('text');
        var filteredData = inputType === 'number' ? data.replace(/\D/g, '') : data;
        var newData = splitStringToLengthArray(filteredData, otpLength);
        inputRefs.forEach(function (inputRef, index) {
            var input = inputRef.current;
            if (input) {
                input.value = newData[index] || '';
            }
        });
        setOtpValue(newData);
        onChange(event, newData);
    };
    var onKeyDownHandler = function (groupIndex) { return function (event) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        var currentRef = inputRefs[groupIndex];
        var previousRef = inputRefs[groupIndex - 1];
        var nextRef = inputRefs[groupIndex + 1];
        switch (event.key) {
            case 'Backspace':
                if (((_a = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _a === void 0 ? void 0 : _a.value.length) === 0) {
                    event.preventDefault();
                    (_b = previousRef === null || previousRef === void 0 ? void 0 : previousRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                }
                break;
            case 'Left':
            case 'ArrowLeft':
                if (((_c = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _c === void 0 ? void 0 : _c.selectionStart) === 0 &&
                    ((_d = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _d === void 0 ? void 0 : _d.selectionEnd) === 0) {
                    event.preventDefault();
                    (_e = previousRef === null || previousRef === void 0 ? void 0 : previousRef.current) === null || _e === void 0 ? void 0 : _e.focus();
                }
                break;
            case 'Right':
            case 'ArrowRight':
                if (((_f = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _f === void 0 ? void 0 : _f.selectionStart) ===
                    ((_g = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _g === void 0 ? void 0 : _g.value.length) &&
                    ((_h = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _h === void 0 ? void 0 : _h.selectionEnd) ===
                        ((_j = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _j === void 0 ? void 0 : _j.value.length)) {
                    event.preventDefault();
                    (_k = nextRef === null || nextRef === void 0 ? void 0 : nextRef.current) === null || _k === void 0 ? void 0 : _k.focus();
                }
                break;
            default:
        }
        if (onKeyDown)
            onKeyDown(event);
    }; };
    return (React.createElement(Layout, { className: className, direction: "column", gap: "xx-small", align: "center" },
        React.createElement(Layout, { gap: inputGap[otpLength] || 'medium' }, items.map(function (index) { return (React.createElement(InputField, __assign({ key: index, type: inputType, inputMode: inputMode, ref: inputRefs[index], id: id + "-" + (index + 1), name: id + "-" + (index + 1), "aria-label": label + " " + (index + 1), maxLength: 1 }, htmlAttributes, { value: otpValue[index], onChange: onChangeHandler(index), onKeyDown: onKeyDownHandler(index), onBlur: onBlurHandler, onFocus: onFocusHandler, onPaste: onPasteHandler, isDisabled: isDisabled, required: isRequired, pattern: pattern, autoComplete: htmlAutoComplete, isValid: isValid, hasError: hasError }))); })),
        getInputFeedback(hasError, errorMessage, isValid, successMessage)));
})();
export var InputOtpPropTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    otpLength: otpLengthPropType,
    defaultValue: PropTypes.arrayOf(PropTypes.string),
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isValid: PropTypes.bool,
    hasError: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    inputType: PropTypes.oneOf(['text', 'number']),
    inputMode: PropTypes.string,
    htmlAutoComplete: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
};
InputOtp.propTypes = InputOtpPropTypes;
var templateObject_1, templateObject_2;
//# sourceMappingURL=InputOtp.js.map